import { Button, Group, Modal, Stack, Text, TextInput, Textarea, TagsInput } from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { useIntegrationApp } from "@membranehq/react";
import { toast } from "sonner";
import { useState } from "react";
import { createMeeting } from "../../services/meetingEndpoint";
import useWorkspaceStore from "../../store/workspace/workspaceStore";

interface MeetingCreationModalProps {
  opened: boolean;
  onClose: () => void;
}

const MeetingCreationModal = ({ opened, onClose }: MeetingCreationModalProps) => {
  const workspace = useWorkspaceStore((state) => state.workspace);
  const integrationApp = useIntegrationApp();

  const [isCreating, setIsCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [attendees, setAttendees] = useState<string[]>([]);
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");

  const handleCreateMeeting = async () => {
    if (!workspace?.id || !title.trim() || !startTime || !endTime) return;

    setIsCreating(true);
    try {
      // Get Google Calendar connection ID
      const calendarConnection = await integrationApp.connection('google-calendar').get();
      const connectionId = calendarConnection?.id;

      if (!connectionId) {
        console.log(calendarConnection);
        toast.error("Connection Error", { description: "No Google Calendar connection found. Please connect your Google Calendar account first." });
        return;
      }

      await createMeeting({
        workspace_id: workspace.id,
        connectionId,
        title: title.trim(),
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
        attendees: attendees.length > 0 ? attendees : undefined,
        description: description.trim() || undefined,
        location: location.trim() || undefined,
      });

      toast.success("Meeting Created", { description: "Your meeting has been created successfully." });

      // Reset form and close modal
      setTitle("");
      setStartTime(null);
      setEndTime(null);
      setAttendees([]);
      setDescription("");
      setLocation("");
      onClose();
    } catch (error) {
      console.error("Failed to create meeting:", error);
      toast.error("Creation Failed", { description: "Failed to create meeting. Please try again." });
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateMeetingClick = () => {
    handleCreateMeeting();
  };

  const isFormValid = title.trim() && startTime && endTime && startTime < endTime;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600} size="lg">Create Meeting</Text>}
      size="lg"
      padding="md"
    >
      <Stack gap="md">
        <TextInput
          label="Meeting Title"
          placeholder="Enter meeting title..."
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
          size="sm"
          required
          data-autofocus
        />

        <Group grow>
          <DateTimePicker
            label="Start Time"
            placeholder="Select start time"
            value={startTime}
            onChange={(value) => setStartTime(value ? new Date(value) : null)}
            size="sm"
            required
            clearable
          />
          <DateTimePicker
            label="End Time"
            placeholder="Select end time"
            value={endTime}
            onChange={(value) => setEndTime(value ? new Date(value) : null)}
            size="sm"
            required
            clearable
          />
        </Group>

        <TagsInput
          label="Attendees"
          placeholder="Add email addresses..."
          value={attendees}
          onChange={setAttendees}
          size="sm"
          data={[]}
        />

        <TextInput
          label="Location"
          placeholder="Meeting location or virtual link..."
          value={location}
          onChange={(e) => setLocation(e.currentTarget.value)}
          size="sm"
        />

        <Textarea
          label="Description"
          placeholder="Meeting agenda and details..."
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          minRows={3}
          maxRows={6}
          autosize
          size="sm"
        />

        <Group justify="flex-end" gap="xs" mt="sm">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateMeetingClick}
            loading={isCreating}
            disabled={!isFormValid}
          >
            Create Meeting
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default MeetingCreationModal;
